import { authGet, authSet, authRemove } from './authStorage'

/**
 * profileRunPending.js — PROD-1192 (L2a). Is the health-profile run right after sign-up still
 * pending? That run belongs to setup: no PIN lock and no away stamp during it, and the normal
 * away/idle rule starts when the patient FIRST reaches Home after it.
 *
 * ★ WHY A MARKER AT ALL. After `finishSignUp` the profile says `onboardingCompleted: true`, so
 *   `isSetupInProgress(profile)` is already false while the (optional) health-profile run is open,
 *   and the profile has no field that says "this patient is in the run right now": a patient who
 *   skipped it long ago looks identical. The marker is written when sign-up finishes and is CONSUMED (read once, then removed) at the
 *   patient's first main-app screen: the first mount of any MobileLayout route (utils/firstHome.js),
 *   goHome, the reload-to-Home resume, and removed by leaving sign-up, login and logout.
 *
 * ★ IT CAN NEVER SKIP THE LOCK FOR A FINISHED PATIENT. It is only read together with the profile:
 *   the marker names THIS user ({ uid, at }), the profile must say sign-up is complete, and the
 *   marker must be younger than PROFILE_RUN_CEILING_MS. A marker that is absent, unreadable, in the
 *   future, too old, another user's or contradicted by the profile means "not in setup". Its only
 *   reader is the first-main-app-screen consume, which restarts the away/idle clocks; it never touches
 *   an existing lock.
 */

export const PROFILE_RUN_KEY = 'orella-profile-run-pending'

/** A run abandoned for longer than this is no longer setup. */
export const PROFILE_RUN_CEILING_MS = 24 * 60 * 60 * 1000

/**
 * Pure. `now` REQUIRED. `marker` is { uid, at } as read by readProfileRunMarker; `userId` is the
 * current user's id. Pending iff the marker names this (non-empty) user, `at` is an integer no
 * older than the ceiling and not in the future, and the profile says sign-up is complete.
 */
export function isProfileRunPending({ marker, userId, profile, now } = {}) {
  if (!Number.isFinite(now)) {
    throw new TypeError('isProfileRunPending: `now` must be a finite epoch')
  }
  if (!marker || typeof marker !== 'object') return false
  const uid = userId === null || userId === undefined ? '' : String(userId)
  if (uid === '' || marker.uid !== uid) return false
  if (!Number.isInteger(marker.at)) return false
  const age = now - marker.at
  if (age < 0 || age > PROFILE_RUN_CEILING_MS) return false
  return !!profile && profile.onboardingCompleted === true
}

/** The stored marker as { uid, at }, or null when absent or unreadable. */
export function readProfileRunMarker() {
  try {
    const raw = authGet(PROFILE_RUN_KEY)
    if (raw === null || raw === undefined) return null
    const m = JSON.parse(String(raw))
    return m && typeof m === 'object' && typeof m.uid === 'string' ? { uid: m.uid, at: m.at } : null
  } catch (_) {
    return null
  }
}

export function markProfileRunPending(userId, now = Date.now()) {
  try { authSet(PROFILE_RUN_KEY, JSON.stringify({ uid: String(userId ?? ''), at: now })) } catch (_) { /* unwritable = not pending = the normal rule */ }
}

export function clearProfileRunPending() {
  try { authRemove(PROFILE_RUN_KEY) } catch (_) {}
}

/**
 * Read the marker ONCE and always remove it. When it was pending, `onPending` runs (it restarts the
 * away/idle clocks). Returns whether it was pending.
 */
export function consumeProfileRun(opts) {
  const o = opts || {}
  let pending = false
  try { pending = isProfileRunPending({ marker: readProfileRunMarker(), userId: o.userId, profile: o.profile, now: Number.isFinite(o.now) ? o.now : Date.now() }) } catch (_) {}
  clearProfileRunPending()
  if (pending && typeof o.onPending === 'function') o.onPending()
  return pending
}
